import cn from 'classnames'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { AttentionSeverityPlate } from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import type { IAttentionStatusesItem } from '../../types'

import styles from './AttentionNextSteps.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps

/** The plate a status line leads with: small, so the steps under it take the room. */
const PLATE_SIZE = 28

interface IAttentionNextStepsGroupProps {
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
  /** Steps the status has beyond the ones listed. */
  restCount?: number
  /** A step already opens the detail, so the line leaves out its own `Details`. */
  hasDetailStep?: boolean
  /** The step rows; absent while they are read, and `null` once there are none to list. */
  children?: ReactNode
}

/**
 * One status of the tile and its next steps: a line with the level's plate, the status and the
 * way into its detail, and the steps under it. The status is a line rather than a card, so the
 * steps — what the reader came to do — take the tile's height.
 */
export const AttentionNextStepsGroup = ({
  item,
  onSelect,
  restCount = 0,
  hasDetailStep = false,
  children,
}: IAttentionNextStepsGroupProps) => {
  const text = useAttentionStatusesText()
  const openDetail = () => onSelect(item.id)

  return (
    <section className={styles.group} aria-label={item.title}>
      <div className={styles.head}>
        {/* A counted status — the manager cockpit's `9 plans …` — puts its count in the plate, as
            its card did; a partner's own status puts the level's word there. */}
        <AttentionSeverityPlate
          severity={item.severity}
          size={PLATE_SIZE}
          className={cn(styles.plate, {
            [styles.plateCount]: item.titleCount !== undefined,
            [styles.plateLongCount]: String(item.titleCount ?? '').length > 2,
          })}
        >
          {item.titleCount !== undefined ? (
            <span>{item.titleCount}</span>
          ) : (
            <Translation span {...text.phrase.severityName(item.severity)} />
          )}
        </AttentionSeverityPlate>

        <span className={styles.title} title={item.title}>
          {item.title}
        </span>

        {restCount > 0 && (
          <button type="button" className={styles.link} onClick={openDetail}>
            <Translation
              span
              text={KEYS.more}
              replaceParams={[{ key: '{count}', value: String(restCount) }]}
            />
          </button>
        )}

        {!hasDetailStep && (
          <button type="button" className={styles.link} onClick={openDetail}>
            <Translation span text={KEYS.details} />
            <i className="kbase kbase-chevron-right" aria-hidden="true" />
          </button>
        )}
      </div>

      {children === null ? (
        <p className={styles.none}>
          <Translation span text={KEYS.none} />
        </p>
      ) : (
        children && <div className={styles.steps}>{children}</div>
      )}
    </section>
  )
}

AttentionNextStepsGroup.displayName = 'AttentionNextStepsGroup'
